import { workerProxy, wrapWorkerRemote } from '@/utils/worker-transport';
import { createDedicatedDownloadWorkerSession } from '@/features/transformers-js/download-verification/dedicated-worker-cleanup';
import type {
  ITransformersJsDownloadWorker,
  ProgressInfo,
  TransformersJsProgressCallback,
  TransformersJsRuntimeArtifactPreparationResult,
} from '@/features/transformers-js/types';

export interface DownloadVerificationRuntimeArtifactPreparationWorkerClient {
  prepareModelRuntimeArtifacts({ modelId, revision, progressCallback }: {
    modelId: string;
    revision: string;
    progressCallback: TransformersJsProgressCallback;
  }): Promise<TransformersJsRuntimeArtifactPreparationResult>;
  dispose(): Promise<void>;
}

export function createDownloadVerificationRuntimeArtifactPreparationWorkerClient(): DownloadVerificationRuntimeArtifactPreparationWorkerClient {
  if (typeof Worker === 'undefined') {
    return {
      async prepareModelRuntimeArtifacts() {
        throw new Error('Download Verification runtime artifact preparation requires a browser Worker');
      },
      async dispose() {
      },
    };
  }

  const worker = new Worker(
    new URL('../download-worker/entry.ts', import.meta.url),
    { type: 'module' },
  );
  const session = createDedicatedDownloadWorkerSession({
    worker,
    createRemote: () => wrapWorkerRemote<ITransformersJsDownloadWorker>({ endpoint: worker }),
  });

  return {
    async prepareModelRuntimeArtifacts({ modelId, revision, progressCallback }) {
      return await session.run({
        operation: ({ remote }) => remote.prepareModelRuntimeArtifacts(
          modelId,
          revision,
          workerProxy({
            // eslint-disable-next-line local-rules-named-args/require-named-args -- Comlink callback is a positional remote boundary.
            value: (info: ProgressInfo) => {
              if (session.isActive()) {
                return progressCallback({ info });
              }
            },
          }),
        ),
      });
    },
    async dispose() {
      session.dispose();
    },
  };
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
